import { Component, Inject, LOCALE_ID, OnInit } from '@angular/core';
import { Router } from '@angular/router';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { CldrIntlService, IntlService } from '@progress/kendo-angular-intl';

import { AuthenticationService } from '../../auth/authentication.service';
import { AuthenticatedUser } from '../../models/auth/authenticated-user.model';
import { MenuItemModel } from '../../models/menu/menu-item.model';
import { MenuItems } from '../../models/menu/menu-items';
import { LanguageModel } from '../../models/navigation/language.model';
import { languagesConst } from '../../models/navigation/languages';
import { UserCompany } from '../../models/users/user-company.model';
import { LocalStorageService } from '../../services/local-storage.service';
import { CustomMessagesService } from '../../services/translation/custom-messages.service';
import { Constants } from '../../util/constants';
import { cloneObject } from '../../util/utils';
import { menuItemsConst } from './drawer.menu-items';

@UntilDestroy()
@Component({
    selector: 'clfl-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})
export class HeaderComponent implements OnInit {
    public companies!: Array<UserCompany>;

    public loggedUserName: string;

    // languages
    public languages!: Array<LanguageModel>;
    public selectedLanguage!: LanguageModel;

    public menuItems: MenuItems = menuItemsConst;

    constructor(
        private router: Router,
        private customMsgService: CustomMessagesService,
        private localStorageService: LocalStorageService,
        public translate: TranslateService,
        @Inject(LOCALE_ID) public localeId: string,
        public intlService: IntlService,
        public authenticationService: AuthenticationService
    ) {
        this.languages = cloneObject(languagesConst);
        this.selectedLanguage = this.languages[1];
        this.localeId = this.selectedLanguage.localeId;
        this.setLocale(this.localeId);

        this.customMsgService.language = this.selectedLanguage.localeId;
    }

    ngOnInit() {
        this.customMsgService.sharedLanguage.pipe(untilDestroyed(this)).subscribe((language) => (this.localeId = language));

        this.authenticationService.currentUser.pipe(untilDestroyed(this)).subscribe((user) => {
            this.loggedUserName = '';
            if (user) {
                this.loggedUserName = user.userResource.userName;
                this.setUserLanguage(user);
            }
        });
    }

    public isRouteSelected(item: MenuItemModel): boolean {
        return this.router.url.startsWith(item.routerLink);
    }

    public onRoute(item: MenuItemModel): void {
        this.menuItems.parents.forEach((element) => {
            element.selected = false;
        });
        item.selected = true;

        this.router.navigate([item.routerLink]);
    }

    public changeLanguage(language: { icon: string; text: string; localeId: string }) {
        this.selectedLanguage = language;
        this.customMsgService.language = this.selectedLanguage.localeId;
        this.setLocale(this.selectedLanguage.localeId);

        let lang = this.selectedLanguage.localeId;
        if (this.selectedLanguage.localeId === Constants.LOCALE_EN) {
            lang = Constants.LOCALE_DEFAULT_LANG;
        }
        this.translate.setDefaultLang(lang);
        this.storeUserLanguage(lang);
    }

    private storeUserLanguage(language: string) {
        if (this.authenticationService.currentUserValue) {
            this.localStorageService.storeUserLanguage(language, this.authenticationService.currentUserValue.userResource.id);
        }
    }

    private setUserLanguage(user: AuthenticatedUser) {
        const userLanguage = this.localStorageService.retrieveUserLanguage(user.userResource.id);

        if (userLanguage) {
            let lang = userLanguage;

            if (userLanguage === Constants.LOCALE_DEFAULT_LANG) {
                lang = Constants.LOCALE_EN;
            }

            this.selectedLanguage = this.languages.find((l: LanguageModel) => l.localeId === lang);
            this.customMsgService.language = this.selectedLanguage.localeId;
            this.localeId = this.selectedLanguage?.localeId;

            this.translate.setDefaultLang(userLanguage);
            this.setLocale(this.localeId);
        }
    }

    public onLogout() {
        this.authenticationService.logout();
        this.router.navigate(['/login'], {
            queryParams: { returnUrl: this.router.url }
        });
    }

    private setLocale(locale: any): void {
        (this.intlService as CldrIntlService).localeId = locale;
    }
}
